import { useState, useEffect, useCallback } from 'react';
import { useUser } from '../components/UserProvider';

/**
 * 관리자 권한 검증 커스텀 훅
 * adminToken을 서버에서 검증하여 실제 관리자 권한이 있는지 확인합니다.
 * 
 * @returns {Object} - { isAdminVerified, verifyAdminAccess, loading, error, showPasswordModal, setShowPasswordModal, handlePasswordSuccess }
 */
const useAdminVerification = () => {
    const { getAuthHeaders, user } = useUser();
    const [isAdminVerified, setIsAdminVerified] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showPasswordModal, setShowPasswordModal] = useState(false);

    /**
     * 서버에서 관리자 권한 재확인
     * /api/admin/verify 엔드포인트를 호출하여 adminToken을 검증합니다.
     */
    const verifyAdminAccess = useCallback(async (showModalOnFailure = true) => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch('/api/admin/verify', {
                headers: getAuthHeaders()
            });
            
            if (response.ok) {
                const data = await response.json();
                const isVerified = data.isAdmin === true;
                setIsAdminVerified(isVerified);
                
                // 서버에서 토큰 삭제를 요청한 경우 (is_admin이 0인 경우)
                if (data.shouldRemoveToken) {
                    localStorage.removeItem('adminToken');
                    setIsAdminVerified(false);
                    return false;
                }
                
                // 검증 실패했지만 이전에 adminToken이 있었다면 (만료됨) 모달 표시
                // 토큰 삭제하지 않고 재인증 기회 제공
                if (!isVerified && showModalOnFailure) {
                    const hadAdminToken = localStorage.getItem('adminToken');
                    if (hadAdminToken) {
                        // 토큰 삭제하지 않음 - 재인증 기회 제공
                        setShowPasswordModal(true);
                    }
                }
                
                return isVerified;
            } else {
                setIsAdminVerified(false);
                
                // 서버가 isAdmin: false를 반환한 경우
                const data = await response.json().catch(() => ({}));
                
                // 서버에서 토큰 삭제를 요청한 경우
                if (data.shouldRemoveToken) {
                    localStorage.removeItem('adminToken');
                    return false;
                }
                
                // 401/403이 아니라 정상 응답이지만 isAdmin: false인 경우
                // 사용자가 실제로 관리자가 아니거나, adminToken이 만료된 경우
                if (showModalOnFailure && data.isAdmin === false) {
                    const hadAdminToken = localStorage.getItem('adminToken');
                    if (hadAdminToken) {
                        // 토큰 삭제하지 않음 - 재인증 기회 제공
                        setShowPasswordModal(true);
                    }
                }
                
                setError('관리자 권한 확인에 실패했습니다.');
                return false;
            }
        } catch (err) {
            console.error('관리자 권한 확인 오류:', err);
            setIsAdminVerified(false);
            setError(err.message || '관리자 권한 확인 중 오류가 발생했습니다.');
            return false;
        } finally {
            setLoading(false);
        }
    }, [getAuthHeaders]);

    /**
     * 암호 입력 성공 시 호출되는 함수
     */
    const handlePasswordSuccess = useCallback(() => {
        // 모달 닫기
        setShowPasswordModal(false);
        
        // 암호 인증 성공 후 다시 검증 (모달은 표시하지 않음)
        verifyAdminAccess(false);
    }, [verifyAdminAccess]);

    // 컴포넌트 마운트 시 자동으로 권한 확인
    useEffect(() => {
        // 로그인한 사용자만 확인
        if (!user) {
            setIsAdminVerified(false);
            setLoading(false);
            return;
        }

        const adminToken = localStorage.getItem('adminToken');
        
        // adminToken이 있을 경우에만 서버 검증 수행
        // adminToken이 없으면 일반 유저로 간주 (is_admin은 노출하지 않음)
        if (adminToken) {
            verifyAdminAccess(true);
        } else {
            setIsAdminVerified(false);
            setLoading(false);
        }
    }, [user, verifyAdminAccess]);

    return {
        isAdminVerified,         // 서버에서 검증된 관리자 권한 여부
        verifyAdminAccess,       // 수동으로 권한을 재확인하는 함수
        loading,                 // 권한 확인 중인지 여부
        error,                   // 오류 메시지
        showPasswordModal,       // 암호 입력 모달 표시 여부
        setShowPasswordModal,    // 모달 표시 상태 설정 함수
        handlePasswordSuccess    // 암호 인증 성공 핸들러
    };
};

export default useAdminVerification;